import { Link, useParams } from "react-router-dom";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import StartProjectCTA from "@/components/feature/StartProjectCTA";
import { BleedSection, BleedSectionMobile } from "@/components/feature/BleedSection";
import { getProject, projects } from "@/mocks/projects";

export default function ProjectDetail() {
  const { slug = "" } = useParams();
  const project = getProject(slug);

  if (!project) {
    return (
      <div className="min-h-screen bg-warm-bg text-ink">
        <SiteNav />
        <div className="pt-40 pb-24 w-full" style={{ paddingLeft: "clamp(20px, 5vw, 96px)", paddingRight: "clamp(20px, 5vw, 96px)" }}>
          <span className="eyebrow">Not Found</span>
          <h1 className="mt-5 font-serif-display font-light" style={{ fontSize: "44px" }}>Project not found.</h1>
          <Link to="/projects" className="arrow-link mt-8 inline-flex text-[12px] tracking-[0.12em] uppercase cursor-pointer">
            Back to Projects
            <i className="ri-arrow-right-line arrow w-4 h-4 flex items-center justify-center" />
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const idx = projects.findIndex(p => p.slug === project.slug);
  const next = projects[(idx + 1) % projects.length];

  return (
    <div className="min-h-screen bg-warm-bg text-ink">
      <SiteNav />

      <section className="w-full pt-[112px] md:pt-[144px] pb-14">
        <div className="w-full" style={{ paddingLeft: "clamp(20px, 5vw, 96px)", paddingRight: "clamp(20px, 5vw, 96px)" }}>
          <div className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase text-ink-soft mb-8">
            <Link to="/" className="hover:text-accent cursor-pointer">Home</Link><span>/</span>
            <Link to="/projects" className="hover:text-accent cursor-pointer">Projects</Link><span>/</span>
            <span>{project.title}</span>
          </div>
          <span className="eyebrow">Selected Work — {project.num}</span>
          <h1 className="mt-5 font-serif-display font-light leading-[1.02]"
              style={{ fontSize: "clamp(38px,4vw,54px)", letterSpacing: "-0.02em" }}>
            {project.title}
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 border-t hairline">
            <div><span className="eyebrow block mb-2">Type</span><span className="text-[15px]">{project.type}</span></div>
            <div><span className="eyebrow block mb-2">Location</span><span className="text-[15px]">{project.location}</span></div>
            <div><span className="eyebrow block mb-2">Year</span><span className="text-[15px]">{project.year}</span></div>
          </div>
        </div>
      </section>

      <section className="w-full">
        <img src={project.image} alt={project.title}
             className="w-screen block" style={{ aspectRatio: "16/9", objectFit: "cover" }} />
      </section>

      <section className="w-full py-24 md:py-28">
        <div className="w-full grid grid-cols-1 md:grid-cols-12 gap-8" style={{ paddingLeft: "clamp(20px, 5vw, 96px)", paddingRight: "clamp(20px, 5vw, 96px)" }}>
          <div className="md:col-span-4"><span className="eyebrow">Overview</span></div>
          <div className="md:col-span-8">
            <p className="font-serif-display font-light leading-[1.3]"
               style={{ fontSize: "clamp(22px,1.8vw,28px)", color: "#1D1A17" }}>
              {project.overview}
            </p>
          </div>
        </div>
      </section>

      <section className="w-full pb-24">
        <img src={project.secondaryImage} alt={`${project.title} — interior`}
             className="w-screen block" style={{ aspectRatio: "16/9", objectFit: "cover" }} />
      </section>

      <section className="w-full pb-24 md:pb-32">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16" style={{ paddingLeft: "clamp(20px, 5vw, 96px)", paddingRight: "clamp(20px, 5vw, 96px)" }}>
          <div>
            <span className="eyebrow">Challenge</span>
            <p className="mt-5 text-ink leading-[1.65]" style={{ fontSize: "16px" }}>{project.challenge}</p>
          </div>
          <div>
            <span className="eyebrow">Approach</span>
            <p className="mt-5 text-ink leading-[1.65]" style={{ fontSize: "16px" }}>{project.approach}</p>
          </div>
        </div>
      </section>

      <section className="w-full">
        <div className={`w-full grid grid-cols-1 ${project.detailImages.length > 1 ? "md:grid-cols-2" : ""} gap-0`}>
          {project.detailImages.map((src, i) => (
            <img key={i} src={src} alt={`${project.title} detail ${i + 1}`}
                 className="w-full block" style={{ aspectRatio: project.detailImages.length > 1 ? "4/3" : "16/9", objectFit: "cover" }} />
          ))}
        </div>
      </section>

      <BleedSection eyebrow="Materials & Craft" imgSrc={project.materialImage}
        imgAlt="Material detail" imgSide="left" bg="#F8F8F8">
        <p className="font-serif-display italic font-light leading-[1.35]"
           style={{ fontSize: "clamp(22px,1.8vw,28px)", color: "#1D1A17" }}>
          Every joint, edge and transition is resolved at the same level of care as the plan.
        </p>
        <p className="mt-6 text-ink-soft leading-[1.7]" style={{ fontSize: "15px" }}>
          Custom joinery, stone transitions and hardware are drawn, sampled and resolved alongside the plan — never left to the site.
        </p>
      </BleedSection>
      <BleedSectionMobile eyebrow="Materials & Craft" imgSrc={project.materialImage}
        imgAlt="Material detail" bg="#F8F8F8">
        <p className="font-serif-display italic font-light leading-[1.35]"
           style={{ fontSize: "22px", color: "#1D1A17" }}>
          Every joint, edge and transition is resolved at the same level of care as the plan.
        </p>
      </BleedSectionMobile>
      <section className="w-full pb-16 border-t hairline pt-12">
        <div className="w-full flex flex-col md:flex-row md:items-center md:justify-between gap-6" style={{ paddingLeft: "clamp(20px, 5vw, 96px)", paddingRight: "clamp(20px, 5vw, 96px)" }}>
          <span className="eyebrow">Next Project</span>
          <Link to={`/projects/${next.slug}`} className="group cursor-pointer flex items-center gap-6">
            <div className="text-right">
              <div className="font-serif-display font-light leading-[1.1]" style={{ fontSize: "28px" }}>{next.title}</div>
              <div className="text-ink-soft mt-1" style={{ fontSize: "13px" }}>{next.type}</div>
            </div>
            <i className="ri-arrow-right-line w-8 h-8 flex items-center justify-center text-ink group-hover:text-accent transition-colors" />
          </Link>
        </div>
      </section>

      <StartProjectCTA />
      <SiteFooter />
    </div>
  );
}